import { Component, inject, signal, effect } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { IonApp, IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  gridOutline, peopleOutline, receiptOutline, cubeOutline,
  statsChartOutline, ticketOutline,
  storefrontOutline, logOutOutline,
  menuOutline, closeOutline, businessOutline, shieldCheckmarkOutline, albumsOutline,
  carOutline, settingsOutline, pricetagOutline, cashOutline,
  chevronDownOutline,
} from 'ionicons/icons';
import { AdminAuthService } from './services/admin-auth.service';
import { PERMISOS_BASE, SeccionAdmin } from './services/mock-data.service';
import { globalErrorMsg } from './services/global-error-handler.service';
import { PedidosService } from './services/pedidos.service';
import { AutorizacionesService } from './services/autorizaciones.service';

interface NavLeaf { kind: 'link'; path: SeccionAdmin; label: string; icon: string; }
interface NavGroup { kind: 'group'; label: string; icon: string; children: NavLeaf[]; }
type NavEntry = NavLeaf | NavGroup;
interface NavSection { section: string; items: NavEntry[]; }

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonApp, RouterOutlet, IonIcon, RouterLink, RouterLinkActive],
})
export class AppComponent {
  private router = inject(Router);
  auth     = inject(AdminAuthService);
  pedSvc   = inject(PedidosService);
  autSvc    = inject(AutorizacionesService);
  sidebarOpen    = signal(false);
  readonly globalError = globalErrorMsg;

  get pendingPedidos()      { return this.pedSvc.pedidos().filter(p => p.status === 'pendiente').length; }
  get pendingAutorizaciones() {
    return this.autSvc.autorizaciones().filter(a => a.status === 'pendiente').length;
  }

  private leaf(path: SeccionAdmin, label: string, icon: string): NavLeaf {
    return { kind: 'link', path, label, icon };
  }

  navSections: NavSection[] = [
    { section: 'General', items: [
      this.leaf('dashboard', 'Dashboard', 'grid-outline'),
    ] },
    { section: 'Ventas', items: [
      this.leaf('clientes',          'Clientes',        'people-outline'),
      this.leaf('pedidos',           'Pedidos',         'receipt-outline'),
      this.leaf('puntos_venta',      'Puntos de venta', 'cash-outline'),
      this.leaf('envios',            'Envíos',          'car-outline'),
    ] },
    { section: 'Catálogo', items: [
      { kind: 'group', label: 'Catálogo', icon: 'cube-outline', children: [
        this.leaf('productos',   'Productos',   'cube-outline'),
        this.leaf('categorias',  'Categorías',  'albums-outline'),
        this.leaf('marcas',      'Marcas',      'pricetag-outline'),
        this.leaf('inventarios', 'Inventarios', 'storefront-outline'),
      ] },
    ] },
    { section: 'Marketing', items: [
      this.leaf('cupones', 'Cupones', 'ticket-outline'),
    ] },
    { section: 'Administración', items: [
      this.leaf('reportes',        'Reportes',        'stats-chart-outline'),
      this.leaf('administradores', 'Administradores', 'shield-checkmark-outline'),
      this.leaf('configuracion',   'Configuración',   'settings-outline'),
    ] },
  ];

  expandedGroups = signal<Record<string, boolean>>({ 'Catálogo': true });

  toggleGroup(label: string, ev?: Event) {
    ev?.stopPropagation();
    this.expandedGroups.update(m => ({ ...m, [label]: !m[label] }));
  }

  get visibleNavSections(): NavSection[] {
    const rol = this.auth.currentUser()?.rol;
    if (!rol) return [];
    const permitidas = PERMISOS_BASE[rol];

    return this.navSections
      .map(sec => ({
        section: sec.section,
        items: sec.items
          .map(item => item.kind === 'link'
            ? item
            : { ...item, children: item.children.filter(c => permitidas.includes(c.path)) })
          .filter(item => item.kind === 'link' ? permitidas.includes(item.path) : item.children.length > 0),
      }))
      .filter(sec => sec.items.length > 0);
  }

  groupHasBadge(children: NavLeaf[]): boolean {
    return children.some(c => this.badgeFor(c.path) > 0);
  }

  badgeFor(path: SeccionAdmin): number {
    switch (path) {
      case 'pedidos':       return this.pendingPedidos;
      case 'puntos_venta':  return this.pendingAutorizaciones;
      default: return 0;
    }
  }

  constructor() {
    addIcons({
      gridOutline, peopleOutline, receiptOutline, cubeOutline,
      statsChartOutline, ticketOutline,
      storefrontOutline, logOutOutline,
      menuOutline, closeOutline, businessOutline, shieldCheckmarkOutline, albumsOutline,
      carOutline, settingsOutline, pricetagOutline,
      cashOutline, chevronDownOutline,
    });
    // Renovar actividad en cualquier interacción del usuario
    const refresh = () => this.auth.refreshActivity();
    document.addEventListener('click',    refresh, { passive: true });
    document.addEventListener('keydown',  refresh, { passive: true });
    document.addEventListener('touchstart', refresh, { passive: true });

    // Autorizaciones pendientes (devoluciones POS) — solo gerente/superadmin
    // pueden verlas, se carga apenas inicia sesión sin necesitar entrar a
    // "Puntos de venta" primero.
    let autorizacionesCargadas = false;
    effect(() => {
      const rol = this.auth.currentUser()?.rol;
      if (!autorizacionesCargadas && (rol === 'gerente' || rol === 'superadmin')) {
        autorizacionesCargadas = true;
        this.autSvc.load('pendiente');
      }
    });
  }

  toggleSidebar() { this.sidebarOpen.update(v => !v); }
  closeSidebar()  { this.sidebarOpen.set(false); }

  logout() {
    this.auth.logout();
    this.router.navigate(['/login'], { replaceUrl: true });
  }
}
